import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { AdministratorItem } from '@mvcp/contracts';
import { inviteUser, listAdministrators } from '../api';

import { Avatar } from './Avatar';
import { FormModal } from './FormModal';
const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: AdministratorItem[] }
  | { kind: 'error'; message: string };

/**
 * Admin Console → Administrators (Story 6.3, FR-30). The System-Administrator
 * governance surface: lists the platform's Administrators — activated admins plus
 * pending admin invitations, each with a status marker — and a focused
 * "Add administrator" form (email only, no company: an admin is cross-tenant).
 * Adding reuses the existing magic-link invite flow (`inviteUser`); on success it
 * surfaces the dev activation link and refreshes the list, where the just-added
 * admin appears as `pending` before they activate (the add is visibly reflected).
 * System-Administrator-only (belt with the API guard).
 */
export function AdministratorsView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Whether the add dialog is open (the form moved out of the page body). */
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const items = await listAdministrators();
      setState({ kind: 'ready', items });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    // Guard against a double-submit (e.g. a fast second Enter before the button's
    // `disabled` re-renders) minting two pending invitations / two live tokens for
    // the same email.
    if (submitting) return;
    setError(null);
    setNotice(null);
    setLink(null);
    setSubmitting(true);
    const submitted = email;
    let added = false;
    try {
      const inv = await inviteUser({ email: submitted, role: 'system_administrator' });
      // Confirm the add independently of `activationLink` (absent in production):
      // a success message is shown regardless; the dev link is an extra when present.
      setNotice(`Invitation sent to ${submitted}`);
      // Close on SUCCESS only; the notice + link render on the page behind it.
      setAddingAdmin(false);
      setLink(inv.activationLink ?? null);
      setEmail('');
      added = true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add administrator');
    } finally {
      setSubmitting(false);
    }
    // Refresh the roster ONLY after a successful add, and OUTSIDE the add's
    // try/catch. `load()` surfaces its own failure via the list's error+Retry
    // branch, so a refresh failure can never turn a successful add into an "add
    // failed" error or wipe the success confirmation. On success the just-added
    // admin appears as `pending` (never a silent no-op).
    if (added) {
      await load();
    }
  }

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
        Administrators
      </h1>
      <p className="muted" style={{ margin: 0 }}>
        System Administrators govern the platform across all tenants. Add another
        administrator by email — they are provisioned through the same magic-link
        flow (with MFA required at sign-in).
      </p>

      <section className="card stack" aria-labelledby="administrators-heading">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h2 id="administrators-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Current administrators
          </h2>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => setAddingAdmin(true)}
          >
            Add an administrator
          </button>
        </div>

        {state.kind === 'loading' && <p className="muted" style={{ margin: 0 }}>Loading…</p>}

        {state.kind === 'error' && (
          <div className="stack" style={{ gap: 8 }} role="alert">
            <p className="muted" style={{ margin: 0 }}>{state.message}</p>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => void load()}
              style={{ alignSelf: 'flex-start' }}
            >
              Retry
            </button>
          </div>
        )}

        {state.kind === 'ready' && state.items.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>No administrators yet.</p>
        )}

        {state.kind === 'ready' && state.items.length > 0 && (
          /* Shared roster shape — `.table-scroll` / `.data-table` in global.css. Same
             instrument as Admin → Users and the cockpit's People list, including the nowrap
             that keeps an email and an ISO date from breaking after their hyphens. */
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((a) => (
                  <tr key={`${a.email}:${a.status}`}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <Avatar id={a.email} name={a.email} />
                        <span data-numeric>{a.email}</span>
                      </span>
                    </td>
                    <td>
                      <span className="status-pill" data-tone={a.status === 'active' ? 'ok' : 'warn'}>
                        <span className="status-pill__dot" aria-hidden="true" />
                        {a.status === 'active' ? 'Active' : 'Pending'}
                      </span>
                    </td>
                    <td data-numeric>{shortDate(a.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {addingAdmin && (
      <FormModal title="Add administrator" titleId="add-admin-heading" onClose={() => setAddingAdmin(false)}>
        <form onSubmit={onSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
          <label className="stack" style={{ gap: 4, flex: '1 1 220px' }}>
            <span className="field-label">Email</span>
            <input
              aria-label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={fieldStyle}
            />
          </label>
          <button type="submit" className="btn-accent" data-surface="accent" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add administrator'}
          </button>
        </form>

        {error && (
          <div role="alert">
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {error}
            </span>
          </div>
        )}
      </FormModal>
      )}
      {/* The activation link is the RESULT of the add and has to be copied, so it lives on
          the page rather than inside the dialog that produced it — closing the dialog on
          success must not take the link with it. */}
      <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
        {notice && (
          <p className="muted" style={{ margin: 0, fontSize: 13, wordBreak: 'break-all' }}>
            {notice}.
            {link && (
              <>
                {' '}
                Activation link (dev — no email sent): <span data-numeric>{link}</span>
              </>
            )}
          </p>
        )}
      </div>
    </>
  );
}
